import { Link, useLocation } from 'react-router'
import { textOn, type LogoRailItem } from '@carouselgg/nexus/logo-rail'
import { ActionLink } from './ActionLink'
import { IdentityMenu } from './IdentityMenu'
import { CreateMenu } from './CreateMenu'
import { MobileLogoStrip } from './MobileLogoStrip'
import type { MainNav } from './useMainNav'
import styles from './AppSidebar.module.css'

/**
 * The main side nav, shared by every surface that shows it: the app shell and
 * an org's management area. One definition, so the two never drift — whatever
 * someone can reach from the home page they can reach from inside an org's
 * settings, in the same place.
 */

/** A team's or org's logo as a nav icon; with none, its initials in its brand colour. */
export function Mark({ item }: { item: LogoRailItem }) {
  return item.logoUrl
    ? <img src={item.logoUrl} alt="" className={styles.mark} />
    : (
      <span
        className={`${styles.mark} ${styles.markLetter}`}
        style={item.color && textOn(item.color) ? { background: item.color, color: textOn(item.color) } : undefined}
        aria-hidden="true"
      >
        {(item.initials || item.label).slice(0, 2).toUpperCase()}
      </span>
    )
}

/**
 * What sits above the main nav: the identity block (trust), Join or Start,
 * and — on a phone, where there is no logo rail — your logos across the top.
 */
export function MainNavHeader({
  nav,
  collapsed,
  onNavigate,
}: {
  nav: MainNav
  collapsed?: boolean
  onNavigate: () => void
}) {
  const location = useLocation()
  const back = encodeURIComponent(location.pathname + location.search)
  return (
    <>
      {nav.isAuthenticated && (
        <>
          <IdentityMenu collapsed={collapsed} onNavigate={onNavigate} />
          <CreateMenu teams={nav.teams} orgs={nav.orgs} collapsed={collapsed} onNavigate={onNavigate} />
        </>
      )}
      {/* Signed out, the identity block's place says how to get one. */}
      {/* Not on the signed-out dashboard, whose own page leads with the same ask. */}
      {!nav.isAuthenticated && !collapsed && location.pathname !== '/dashboard' && (
        <div className={styles.joinCard}>
          <span className={styles.joinTitle}>Join Carousel</span>
          <span className={styles.joinBody}>One verified profile to sign up solo, start a team or club, or run a league.</span>
          <ActionLink to={`/signup?redirect=${back}`} className={styles.joinSignup}>Sign up</ActionLink>
          <Link to={`/login?redirect=${back}`} className={styles.joinLogin} onClick={onNavigate}>
            Have an account? Log in
          </Link>
        </div>
      )}
      {/* Phones have no logo rail: the logos sit across the top of the drawer. */}
      <MobileLogoStrip groups={nav.groups} onNavigate={onNavigate} className={styles.mobileOnly} />
    </>
  )
}
